import type { Timestamp } from 'firebase/firestore';
import type { WorkoutAssignment, WorkoutTemplate } from '../../types';
import { StatusBadge } from './StatusBadge';
import { CARDS, TEXT } from '../../styles/classNames';
import { formatDateTime } from '@/shared/utils/dates';

interface AssignmentListProps {
  assignments: WorkoutAssignment[];
  templates: WorkoutTemplate[];
  clientNameMap?: Map<string, string>;
  hideClientName?: boolean;
}

function formatTimestamp(timestamp: Timestamp) {
  try {
    return formatDateTime(timestamp.toDate());
  } catch {
    return 'N/A';
  }
}

export function AssignmentList({ assignments, templates, clientNameMap, hideClientName = false }: AssignmentListProps) {
  const templateLookup = new Map(templates.map((tpl) => [tpl.id, tpl]));

  return (
    <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
      {assignments.length === 0 && (
        <p className={TEXT.small}>
          Nothing queued. Add templates to the client's training plan and schedule to start their week.
        </p>
      )}
      {assignments.map((assignment) => {
        const templateName =
          assignment.workoutTemplateName ||
          templateLookup.get(assignment.workoutTemplateId)?.name ||
          'Template';
        const clientName = clientNameMap?.get(assignment.clientId) || assignment.clientId;
        return (
          <div
            key={assignment.id}
            className={`${CARDS.nested} flex items-start justify-between gap-3`}
          >
            <div className="flex-1 min-w-0">
              <p className={TEXT.h3}>{templateName}</p>
              {!hideClientName && (
                <p className={TEXT.muted}>Client: {clientName}</p>
              )}
              <p className={`${TEXT.small} mt-1`}>
                {formatTimestamp(assignment.scheduledFor as Timestamp)}
              </p>
            </div>
            <StatusBadge status={assignment.status} />
          </div>
        );
      })}
    </div>
  );
}
